if (!customElements.get('annur-category-carousel')) {
  class AnnurCategoryCarousel extends HTMLElement {
    connectedCallback() {
      this.stopAutoplay();
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.slides = [...this.querySelectorAll('[data-annur-category-slide]')];
      this.tabs = [...this.querySelectorAll('[data-annur-category-tab]')];
      this.previousButton = this.querySelector('[data-annur-category-previous]');
      this.nextButton = this.querySelector('[data-annur-category-next]');
      this.controls = this.querySelector('[data-annur-category-controls]');
      this.status = this.querySelector('[data-annur-category-status]');
      this.currentIndex = 0;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      const configuredSeconds = Number.parseInt(this.dataset.changeSeconds, 10);
      this.changeInterval =
        Math.min(15, Math.max(3, Number.isFinite(configuredSeconds) ? configuredSeconds : 5)) * 1000;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.nonAutoplayQuery = window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)');
      this.userPaused = false;
      this.interactionPaused = false;
      this.assistivePaused = document.body.classList.contains('a11y-reduce-motion');
      this.dataset.ready = 'true';
      this.controls?.removeAttribute('hidden');

      if (!this.slides.length) return;

      this.show(0, false);
      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener(
          'click',
          () => {
            this.show(index, true);
            this.pauseAfterUserAction();
          },
          { signal },
        );
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index), { signal });
      });
      this.addEventListener('mouseenter', () => this.setInteractionPaused(true), { signal });
      this.addEventListener('mouseleave', () => this.setInteractionPaused(false), { signal });
      this.addEventListener('focusin', () => this.setInteractionPaused(true), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
        },
        { signal },
      );
      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
      this.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
      this.addEventListener(
        'pointercancel',
        () => {
          this.pointerStart = undefined;
          this.restartAutoplay();
        },
        { signal },
      );
      this.motionQuery.addEventListener(
        'change',
        () => {
          this.restartAutoplay();
        },
        { signal },
      );
      this.nonAutoplayQuery.addEventListener(
        'change',
        () => {
          this.restartAutoplay();
        },
        { signal },
      );
      document.addEventListener('visibilitychange', () => this.restartAutoplay(), { signal });
      window.addEventListener(
        'annur:reduce-motion-change',
        (event) => {
          this.assistivePaused = event.detail.active;
          this.restartAutoplay();
        },
        { signal },
      );
      this.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        },
        { capture: true, signal },
      );
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
          if (index >= 0) {
            this.setInteractionPaused(true);
            this.show(index, false);
          }
        },
        { signal },
      );
      this.addEventListener('shopify:block:deselect', () => this.setInteractionPaused(false), { signal });
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.abortController?.abort();
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.slides.length <= 1 ||
        this.userPaused ||
        this.interactionPaused ||
        this.assistivePaused ||
        this.motionQuery.matches ||
        this.nonAutoplayQuery.matches ||
        document.hidden
      )
        return;
      this.autoplayTimer = window.setInterval(() => {
        this.show((this.currentIndex + 1) % this.slides.length, false);
      }, this.changeInterval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    setInteractionPaused(paused) {
      this.interactionPaused = paused;
      this.restartAutoplay();
    }

    pauseAfterUserAction() {
      this.userPaused = true;
      this.restartAutoplay();
    }

    handleTabKeydown(event, index) {
      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      let targetIndex = index;
      if (direction) targetIndex = (index + direction + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.tabs.length - 1;
      if (targetIndex === index && !direction && event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();
      this.show(targetIndex, true);
      this.tabs[targetIndex]?.focus();
      this.pauseAfterUserAction();
    }

    move(direction) {
      this.show((this.currentIndex + direction + this.slides.length) % this.slides.length, true);
      this.pauseAfterUserAction();
    }

    handlePointerDown(event) {
      if (event.button !== 0 || event.target.closest('button')) return;
      this.stopAutoplay();
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart) return;
      const { x, y } = this.pointerStart;
      this.pointerStart = undefined;
      this.restartAutoplay();
      const deltaX = event.clientX - x;
      const deltaY = event.clientY - y;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.suppressClick = true;
      this.move(deltaX < 0 ? 1 : -1);
    }

    show(index, announce) {
      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.dataset.active = String(active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (announce && this.status)
        this.status.textContent = `Colección ${index + 1} de ${this.slides.length}: ${this.slides[index].dataset.title}`;
    }
  }

  customElements.define('annur-category-carousel', AnnurCategoryCarousel);
}
